import { ApiCompatibilityService } from './api-compatibility.service';
import { MaintenanceService } from './maintenance.service';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject, share } from 'rxjs';
import { WebSocketChannel } from '../../shared/network/web-socket-channel';
import { config } from '../../../environments/environment';
import { nativeTenantDomain } from '../../shared/helpers/tenant';
import { warn } from '@4players/odin-common';

import { parseServerEvent, ClientEventSchema } from '@andor/contracts/notifications';
import type { ServerEvent as WireServerEvent, ClientEvent } from '@andor/contracts/notifications';
export type { RoomDescriptor } from '@andor/contracts/rooms';
export type { ClientEvent } from '@andor/contracts/notifications';
export type ServerEvent = Exclude<WireServerEvent, { type: 'pong' }>;

/**
 * How often to send a `ping` if nothing else has come down the wire. Comfortably
 * below typical proxy / load-balancer idle timeouts (AWS ALB 60s, GCP 30s).
 */
const PING_INTERVAL_MS = 30_000;
/** Watchdog cadence — granularity of the dead-connection check. */
const WATCHDOG_INTERVAL_MS = 10_000;
/** Treat the connection as dead if no inbound message arrived in this window. */
const DEAD_AFTER_MS = 70_000;
/** Skip an explicit ping if a server message arrived this recently. */
const PING_DEBOUNCE_MS = 25_000;

/**
 * Owns the WebSocket to `/v1/notifications`. Single source of truth for
 * realtime traffic with the backend — both inbound (settings, permissions,
 * room lifecycle) and outbound (keepalive, rejoin).
 */
@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private maintenance = inject(MaintenanceService);
  private compatibility = inject(ApiCompatibilityService);
  private opening?: AbortController;
  constructor() {
    this.maintenance.entered.subscribe(() => this.close());
  }
  private _channel?: WebSocketChannel;
  private _connecting?: Promise<void>;
  private _generation = 0;
  /** Credential the current channel was opened with (see {@link reconnectWith}). */
  private _authToken?: string;

  private readonly _serverEvents = new Subject<ServerEvent>();
  /**
   * Parsed JSON messages from the server. Subscribers should switch on
   * `event.type` and validate `data` against their own schema where needed.
   */
  readonly serverEvents$: Observable<ServerEvent> = this._serverEvents.pipe(share());

  private readonly _onReconnect = new Subject<void>();
  /** Fires when the underlying WebSocket reconnects after a drop. */
  readonly onReconnect$ = this._onReconnect.asObservable();

  private readonly _onConnected = new Subject<void>();
  /** Fires on initial connection AND on every successful reconnect. */
  readonly onConnected$ = this._onConnected.asObservable();

  /** Timestamp of the last inbound message — any message, not just pong. */
  private _lastInboundAt = 0;
  private _pingTimer: ReturnType<typeof setInterval> | null = null;
  private _watchdogTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Opens the channel. Idempotent — subsequent calls are no-ops while the
   * previous channel is alive.
   */
  connect(authToken: string, recoveryGeneration?: number): Promise<void> {
    if (this.compatibility.unsupported())
      return Promise.reject(new Error('This app needs an update'));
    if (this.maintenance.active() && !this.maintenance.isRecoveryCurrent(recoveryGeneration))
      return Promise.reject(new Error('Backend recovery is not authorized'));
    if (this._channel) return Promise.resolve();
    if (this._connecting) return this._connecting;
    const pending = this._open(authToken, this._generation, recoveryGeneration).finally(() => {
      if (this._connecting === pending) this._connecting = undefined;
    });
    this._connecting = pending;
    return pending;
  }

  private async _open(
    authToken: string,
    generation: number,
    recoveryGeneration?: number
  ): Promise<void> {
    // Browser WebSockets cannot carry the tenant header, so the native app
    // names its tenant as a query parameter on this one URL.
    const domain = nativeTenantDomain();
    const url =
      `${config.backendWebSocketUrl}/v1/notifications?uuid=${encodeURIComponent(authToken)}` +
      (domain ? `&domain=${encodeURIComponent(domain)}` : '');
    this.opening = new AbortController();
    const channel = await WebSocketChannel.create(url, {
      signal: this.opening.signal,
      beforeConnect: async () => {
        if (!(await this.maintenance.check())) return false;
        if (this.maintenance.active() && !this.maintenance.isRecoveryCurrent(recoveryGeneration))
          return false;
        try {
          await this.compatibility.check(this.opening?.signal);
          return true;
        } catch {
          return false;
        }
      },
    });
    // A failed startup or identity change may cancel a still-opening socket.
    if (generation !== this._generation) {
      channel.close();
      throw new DOMException('Canceled', 'AbortError');
    }
    this._authToken = authToken;

    channel.onMessage$.subscribe((raw) => {
      // Any byte from the server counts as a heartbeat — record before parse.
      this._lastInboundAt = Date.now();

      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch (err) {
        warn(`[realtime] dropping non-JSON message`, { err, raw });
        return;
      }
      const event = parseServerEvent(parsed);
      if (!event || event.type === 'pong') return;
      if (event.type === 'maintenance') this.maintenance.accept(event.data);
      this._serverEvents.next(event);
    });

    channel.onReconnect$.subscribe(() => this._onReconnect.next());
    channel.onConnected$.subscribe(() => {
      // Fresh socket — reset liveness so the watchdog doesn't immediately
      // re-kill it. Fires on both initial connect and reconnect.
      this._lastInboundAt = Date.now();
      this._onConnected.next();
      window.dispatchEvent(new Event('andor:connected'));
    });

    this._channel = channel;
    this._lastInboundAt = Date.now();
    this._startHeartbeat();
    this._onConnected.next();
    window.dispatchEvent(new Event('andor:connected'));
  }

  /**
   * Re-key the socket to a new credential. The server binds a socket to the
   * identity presented at the handshake and never re-keys it, so an identity
   * change (session token re-mint for a different uuid) MUST tear the channel
   * down and dial again — otherwise permission pushes resolve against the
   * stale identity. No-op when the token is unchanged and the channel is
   * alive. `_serverEvents` is a persistent Subject, so existing
   * `serverEvents$` subscriptions survive the swap.
   */
  async reconnectWith(authToken: string): Promise<void> {
    if (this._channel && authToken === this._authToken) return;
    this.close();
    await this.connect(authToken);
  }

  /**
   * Send a typed event to the backend. Silently drops if the channel is not
   * open — the room-rejoin flow handles re-establishing state on reconnect.
   */
  send(event: ClientEvent): void {
    if (!this.compatibility.unsupported()) this._channel?.send(ClientEventSchema.parse(event));
  }

  close(): void {
    this.opening?.abort();
    this._generation++;
    this._connecting = undefined;
    this._stopHeartbeat();
    this._channel?.close();
    this._channel = undefined;
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this._pingTimer = setInterval(() => {
      if (Date.now() - this._lastInboundAt < PING_DEBOUNCE_MS) return;
      this.send({ type: 'ping' });
    }, PING_INTERVAL_MS);
    this._watchdogTimer = setInterval(() => {
      if (Date.now() - this._lastInboundAt <= DEAD_AFTER_MS) return;
      // Drop the socket; the channel's auto-reconnect (exponential backoff +
      // window.online listener) takes over. Use forceReconnect rather than
      // close() so the channel isn't marked permanently dead.
      warn(`[realtime] no traffic in ${DEAD_AFTER_MS}ms — forcing reconnect`);
      this._channel?.forceReconnect();
    }, WATCHDOG_INTERVAL_MS);
  }

  private _stopHeartbeat(): void {
    if (this._pingTimer !== null) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
    if (this._watchdogTimer !== null) {
      clearInterval(this._watchdogTimer);
      this._watchdogTimer = null;
    }
  }
}
